import React from 'react';
import { cn } from './lib/utils';

type ProgressBarColor =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'info'
  | 'warning'
  | 'error';

interface ProgressBarProps {
  value: number;
  max?: number;
  className?: string;
  showLabel?: boolean;
  animated?: boolean;
  labelClassName?: string;
  color?: ProgressBarColor;
}

const colorVariants = {
  primary: {
    bg: 'bg-primary',
    bgLight: 'bg-primary/30',
  },
  secondary: {
    bg: 'bg-secondary',
    bgLight: 'bg-secondary/30',
  },
  success: {
    bg: 'bg-success',
    bgLight: 'bg-success/30',
  },
  info: {
    bg: 'bg-info',
    bgLight: 'bg-info/30',
  },
  warning: {
    bg: 'bg-warning',
    bgLight: 'bg-warning/30',
  },
  error: {
    bg: 'bg-error',
    bgLight: 'bg-error/30',
  },
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  className,
  showLabel = true,
  animated = false,
  labelClassName,
  color = 'primary',
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);
  const colorVariant = colorVariants[color];

  const sweepAnimation = `
    @keyframes sweep-lr {
      0% {
        transform: translateX(-100%);
      }
      100% {
        transform: translateX(100%);
      }
    }
    .animate-sweep-lr {
      position: relative;
      overflow: hidden;
    }
    .animate-sweep-lr::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, 
        transparent 0%, 
        rgba(255, 255, 255, 0.2) 50%, 
        transparent 100%
      );
      animation: sweep-lr 2s linear infinite;
      pointer-events: none;
    }
  `;

  return (
    <>
      <style>{sweepAnimation}</style>
      <div className={cn('w-full', className)}>
        <div className={cn('w-full rounded-full', colorVariant.bgLight)}>
          <div
            className={cn(
              colorVariant.bg,
              'text-white text-xs font-bold text-center p-0.5 leading-none rounded-full transition-all duration-500 ease-out',
              animated && 'animate-sweep-lr',
              labelClassName
            )}
            style={{ width: `${percentage}%` }}
          >
            {showLabel && percentage >= 10 && `${Math.round(percentage)}%`}
          </div>
        </div>
      </div>
    </>
  );
};

export default ProgressBar;
